import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
// DTC-L: route Passport-sync write timestamps through the canonical
// clock so persisted technician_type / employment lifecycle writes are
// deterministic under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PassportAudienceCard } from '@/components/passport/PassportAudienceCard';
import { Briefcase, User, Loader2, Rocket, GraduationCap, School, Home } from 'lucide-react';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';
import { usePermissions } from '@/hooks/usePermissions';
import { membershipRoleToPassportRole } from '@/lib/beta/canonicalRoles';
import { PassportConnectToggle } from '@/components/connect/PassportConnectToggle';
import { usePassportProfile, useCreatePassportProfile } from '@/hooks/useConnectApi';
import { useMySchoolMembership } from '@/hooks/useSchoolApi';

import { PassportSnapshotCard } from '@/components/passport/PassportSnapshotCard';
import { PassportEmploymentHistory } from '@/components/passport/PassportEmploymentHistory';
import { PassportProficiencySection } from '@/components/passport/PassportProficiencySection';
import { fetchLiveProficiencyClasses, portableProvenanceClassOf } from '@/lib/passport/proficiencyProvenance';
import { PassportDiagnosticCompetency } from '@/components/passport/PassportDiagnosticCompetency';
import { PassportSkillHeatmap } from '@/components/passport/PassportSkillHeatmap';
import { PassportPerformanceDNA } from '@/components/passport/PassportPerformanceDNA';
import { PassportTimeSignature } from '@/components/passport/PassportTimeSignature';
import { PassportTicketLedger } from '@/components/passport/PassportTicketLedger';
import { PassportFutureInsights } from '@/components/passport/PassportFutureInsights';
import { PassportLearningCenter } from '@/components/passport/PassportLearningCenter';
import { PassportCoachingNotes } from '@/components/passport/PassportCoachingNotes';
import { PassportInspectionMetrics } from '@/components/passport/PassportInspectionMetrics';
import { PassportRoleTracks } from '@/components/passport/PassportRoleTracks';
import { ToolboxSection } from '@/components/toolbox/ToolboxSection';
import { useDirectTechnicianMetrics } from '@/hooks/useDirectTechnicianMetrics';
import { TechnicianBackgroundCard } from '@/components/technicians/TechnicianBackgroundCard';
import { PassportCertificationsSection } from '@/components/passport/PassportCertificationsSection';
import { AdvisorActivityCard } from '@/components/advisor-passport/AdvisorActivityCard';
import { PassportExperienceSection } from '@/components/passport/PassportExperienceSection';
import { PassportReferencesSection } from '@/components/passport/PassportReferencesSection';
import { PassportEducationSection } from '@/components/passport/PassportEducationSection';
import { PassportEligibilitySection } from '@/components/passport/PassportEligibilitySection';
import { PassportSchoolSection } from '@/components/connect/PassportSchoolSection';
import { StudentVerificationSummary } from '@/components/connect/StudentVerificationSummary';
import { resolveTechnicianTypeTitle, type TechnicianTypeKey } from '@/utils/resolveTechnicianTypeTitle';
import { ErrorBoundary } from '@/components/error/ErrorBoundary';
import { PassportSectionFallback } from '@/components/passport/PassportSectionFallback';
import { PassportSelfViewFloor } from '@/components/passport/PassportSelfViewFloor';
import { PassportPensieveSummary } from '@/components/passport/PassportPensieveSummary';
import { PassportPensieveInsights } from '@/components/passport/PassportPensieveInsights';
import { PassportPensieveCoaching } from '@/components/passport/PassportPensieveCoaching';
import { useTechnicianPensieveStats } from '@/hooks/useTechnicianPensieveStats';
import { usePassportWorkLenses } from '@/hooks/passport/usePassportWorkLenses';
import { selectPassportWorkLens, type PassportWorkLens } from '@/lib/passport/workLens';
import { PassportWorkLensSwitcher } from '@/components/passport/PassportWorkLensSwitcher';
import { PassportLensSection } from '@/components/passport/PassportLensSection';
import { PassportDetailWorkEvidence } from '@/components/passport/PassportDetailWorkEvidence';

/**
 * Start Passport flow for users with no technician_profiles and no passport_profiles.
 */
// Passport roles (lowercase) — must stay in sync with the
// `passport_profiles.roles` CHECK constraint and with
// `ALL_PASSPORT_ROLES` in src/lib/beta/canonicalRoles.ts. The
// leadership widening migration that ships with this batch adds
// `owner` and `org_admin` so invited Owners and Org Admins can land
// on their own Passport identity rather than getting demoted to
// `manager`. Owner / Org Admin Passport pages are not yet built —
// the StartPassportFlow + PassportHome rendering falls back to the
// leadership/manager surface until dedicated views exist (TODO:
// build OwnerPassport / OrgAdminPassport).
const ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'org_admin', label: 'Org Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'advisor', label: 'Advisor / Service Writer' },
  { value: 'technician', label: 'Technician' },
  { value: 'student', label: 'Student' },
  { value: 'instructor', label: 'Instructor' },
  { value: 'school_admin', label: 'School Admin' },
] as const;

function SchoolPortalButton({ navigate }: { navigate: (path: string) => void }) {
  const { data: membership } = useMySchoolMembership();
  if (!membership) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => navigate('/school')}
      className="gap-1.5"
    >
      <GraduationCap className="h-4 w-4" />
      School Portal
    </Button>
  );
}

function AdminSchoolsButton({ navigate }: { navigate: (path: string) => void }) {
  const { data: isAdmin } = useIsInternalAdmin();
  if (!isAdmin) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => navigate('/admin/schools')}
      className="gap-1.5"
    >
      <School className="h-4 w-4" />
      Admin: Schools
    </Button>
  );
}

function StartPassportFlow({
  userEmail,
  createPassport,
  signOut,
  navigate,
  invitedPassportRole,
}: {
  userEmail: string;
  onCreated: () => void;
  createPassport: ReturnType<typeof useCreatePassportProfile>;
  signOut: () => void;
  navigate: (path: string) => void;
  /**
   * Passport role derived from the user's server-verified membership
   * (NOT from localStorage). When provided, the role picker is locked
   * to this value — invited users can't free-select. Falsy = standalone
   * Passport creation, free selection remains.
   */
  invitedPassportRole: string | null;
}) {
  const [displayName, setDisplayName] = useState(userEmail.split('@')[0] || '');
  const isLocked = !!invitedPassportRole;
  // When locked, seed the role state with exactly the invited role so
  // the create-mutation receives the right payload even though every
  // checkbox is disabled.
  const [roles, setRoles] = useState<string[]>(
    isLocked && invitedPassportRole ? [invitedPassportRole] : [],
  );
  const [homeZip, setHomeZip] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createPassport.mutate({
      display_name: displayName.trim(),
      // When locked, always submit the invited role even if local
      // state somehow drifted (defense in depth).
      roles: isLocked && invitedPassportRole ? [invitedPassportRole] : roles,
      home_zip: homeZip.trim() || undefined,
    });
  };

  // Friendly label for the locked role in the helper copy. Falls back
  // to the raw key so a future passport-role addition still renders.
  const invitedRoleLabel = invitedPassportRole
    ? (ROLE_OPTIONS.find((r) => r.value === invitedPassportRole)?.label ?? invitedPassportRole)
    : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-lg space-y-6">
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Create your OptiBay Passport</CardTitle>
            </div>
            <CardDescription className="space-y-2">
              <span className="block">
                Your Passport is your professional profile inside OptiBay. It connects
                your account to your shop role, permissions, training/certifications, and
                future workforce history.
              </span>
              {isLocked && (
                <span
                  className="block text-foreground"
                  data-testid="passport-start-invite-explainer"
                >
                  Because you joined through a beta invitation, your initial role has
                  already been assigned from your invite
                  {invitedRoleLabel ? ` (${invitedRoleLabel})` : ''}.
                </span>
              )}
            </CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Display Name</Label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Your Roles</Label>
                <p className="text-xs text-muted-foreground">
                  {isLocked
                    ? 'Your initial Passport role is set by your beta invitation. You can request changes later from an administrator.'
                    : "Select roles you're qualified for or interested in (optional for now)."}
                </p>
                {ROLE_OPTIONS.map((role) => {
                  const checked = isLocked
                    ? role.value === invitedPassportRole
                    : roles.includes(role.value);
                  return (
                    <div key={role.value} className="flex items-center gap-2">
                      <Checkbox
                        id={`start-role-${role.value}`}
                        checked={checked}
                        disabled={isLocked}
                        data-testid={`start-role-checkbox-${role.value}`}
                        onCheckedChange={(next) => {
                          if (isLocked) return;
                          setRoles((prev) =>
                            next ? [...prev, role.value] : prev.filter((r) => r !== role.value),
                          );
                        }}
                      />
                      <Label
                        htmlFor={`start-role-${role.value}`}
                        className={`font-normal ${isLocked ? 'cursor-default' : 'cursor-pointer'}`}
                      >
                        {role.label}
                      </Label>
                    </div>
                  );
                })}
              </div>

              <div className="space-y-1.5">
                <Label>Home ZIP (optional)</Label>
                <Input
                  value={homeZip}
                  onChange={(e) => setHomeZip(e.target.value)}
                  placeholder="e.g. 90210"
                  maxLength={10}
                />
                <p className="text-xs text-muted-foreground">Used for proximity matching if you enable visibility later.</p>
              </div>

              <Button type="submit" className="w-full" disabled={createPassport.isPending || !displayName.trim()}>
                {createPassport.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Passport
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="grid gap-3">
          <Button
            variant="outline"
            className="justify-start gap-3 h-auto py-4"
            onClick={() => navigate('/account-settings')}
          >
            <User className="h-5 w-5 text-muted-foreground" />
            <div className="text-left">
              <div className="font-medium">Account Settings</div>
              <div className="text-sm text-muted-foreground">Manage your profile and preferences</div>
            </div>
          </Button>
        </div>

        <Button onClick={() => signOut()} variant="ghost" className="w-full text-muted-foreground">
          Sign Out
        </Button>
      </div>
    </div>
  );
}

/**
 * Passport Mode landing page.
 * Shows the user's own Technician Passport if they have a profile,
 * otherwise shows a landing card with rejoin instructions.
 */
const PassportHome = () => {
  // FIX-R38-CRIT-1 / DOC: Passport remains shop-agnostic. We pull
  // hasSchoolMembership purely to surface a one-line "go to school portal"
  // CTA for users whose primary identity also has school access. We do
  // NOT gate any passport rendering on it.
  const { user, signOut, userShops, hasSchoolMembership } = useAuth();
  const navigate = useNavigate();
  const connectEnabled = useFeatureFlag('optibayConnect');
  const { data: passportProfile, isLoading: passportLoading } = usePassportProfile();
  const createPassport = useCreatePassportProfile();
  // Server-verified membership role drives the StartPassportFlow lock.
  // Read once at the page level so StartPassportFlow stays a dumb prop
  // consumer (testable without React-Query / Supabase scaffolding).
  const { role: activeMembershipRole } = usePermissions();
  const invitedPassportRole = membershipRoleToPassportRole(activeMembershipRole ?? null);
  // Query technician_profiles directly via user_id (survives decoupling)
  const { data: snapshotProfile, isLoading: profileLoading } = useQuery({
    queryKey: ['passport-self-profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('technician_profiles')
        .select('*')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Live overlay: prefer active linked technician row if attached
  const liveTechEnabled = !!user?.id && !!snapshotProfile;
  const liveTechQuery = useQuery({
    queryKey: ['passport-live-tech', user?.id],
    queryFn: async () => {
      // Find active technician_users link for this user
      const { data: links, error: linkErr } = await supabase
        .from('technician_users')
        .select('technician_id')
        .eq('user_id', user!.id);
      if (linkErr || !links?.length) return null;

      // Find an active technician among linked rows
      const techIds = links.map(l => l.technician_id);
      const { data: techs, error: techErr } = await supabase
        .from('technicians')
        .select('id, shop_id, name, proficiencies, employment_status')
        .in('id', techIds)
        .eq('employment_status', 'Active')
        .limit(1);

      if (techErr || !techs?.length) return null;
      return techs[0];
    },
    enabled: liveTechEnabled,
  });
  const liveTech = liveTechQuery.data;

  // ── CORE-C GAP-005: settled identity, not merely a present id ──────────
  //
  // `liveTech?.id` is part of the proficiency query KEY, so before this
  // lookup resolved the query ran with the id absent (snapshot branch) and
  // again once it arrived (live branch): a duplicate round trip whose first
  // result is discarded, and a visible flip of the proficiency set under
  // the reader.
  //
  // Gating on `!!liveTech?.id` would be wrong - a subject with NO live
  // technician is a legitimate state that must still read the snapshot.
  // What the dependent query needs is that the lookup has SETTLED, which is
  // three-valued: unresolved / resolved-with / resolved-without.
  //
  // `isFetched` covers success AND error, so a failed lookup keeps today's
  // behaviour (fall through to the snapshot) rather than silently disabling
  // proficiency - failure semantics are GAP-003's and are not changed here.
  const liveIdentitySettled = !liveTechEnabled || liveTechQuery.isFetched;

  // Merge: live tech overlays snapshot for display
  const profile = snapshotProfile ? {
    ...snapshotProfile,
    ...(liveTech ? { full_name: liveTech.name, is_active: true } : {}),
  } : null;

  const profileId = profile?.id;

  // Fetch employment history.
  // FIX-R37-MED-1: collapse the per-row N+1 into two batched queries —
  // one to fetch ALL active memberships, one for the most recent removed
  // membership per shop. Previously the code fired 2 queries per
  // open-ended history row; a user with 20 historical shops triggered 40
  // round-trips just to render this card.
  // CORE-C GAP-003 / HRD-002: `isError` is captured, not discarded.
  //
  // React Query does NOT throw on a failed fetch — it sets `isError` and leaves
  // `data` undefined. So the 23 `ErrorBoundary` wrappers below never fire for a
  // failed read; they catch render throws. The `|| []` at each render site then
  // turned the failure into an empty array, and the person was told
  // "No employment history available" because a request had failed.
  const { data: employmentHistory, isError: employmentHistoryFailed } = useQuery({
    queryKey: ['technician-employment', profileId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('technician_employment_history')
        .select(`*, shop:shops(name)`)
        .eq('technician_profile_id', profileId!)
        .order('start_date', { ascending: false });
      if (error) throw error;
      if (!data) return [];

      const openEntries = data.filter((emp: any) => !emp.end_date);
      if (openEntries.length === 0) return data;

      const openShopIds = Array.from(
        new Set(openEntries.map((emp: any) => emp.shop_id).filter(Boolean))
      );

      // Batched queries: all active + all removed memberships for the
      // open-history shops in one round-trip each.
      const [activeRes, removedRes] = await Promise.all([
        supabase
          .from('memberships')
          .select('shop_id')
          .eq('user_id', user!.id)
          .in('shop_id', openShopIds)
          .eq('status', 'active'),
        supabase
          .from('memberships')
          .select('shop_id, removed_at')
          .eq('user_id', user!.id)
          .in('shop_id', openShopIds)
          .eq('status', 'removed')
          .order('removed_at', { ascending: false }),
      ]);

      const activeShopIds = new Set((activeRes.data || []).map((r: any) => r.shop_id));
      // Pick the most recent removed_at per shop (server already sorted desc).
      const latestRemovedByShop = new Map<string, string>();
      for (const r of removedRes.data || []) {
        if (!latestRemovedByShop.has(r.shop_id) && r.removed_at) {
          latestRemovedByShop.set(r.shop_id, r.removed_at);
        }
      }

      return data.map((emp: any) => {
        if (emp.end_date) return emp;
        if (activeShopIds.has(emp.shop_id)) return emp;
        return {
          ...emp,
          end_date: latestRemovedByShop.get(emp.shop_id) || clock.now().toISOString(),
        };
      });
    },
    enabled: !!profileId,
  });

  // Fetch proficiencies — prefer live tech data if attached
  const { data: proficiencies, isError: proficienciesFailed } = useQuery({
    queryKey: ['technician-proficiencies', profileId, liveTech?.id],
    queryFn: async () => {
      // If live tech is attached, derive proficiencies from the technicians row
      if (liveTech?.proficiencies) {
        const liveProf = liveTech.proficiencies as Record<string, number>;
        // CORE-C GAP-007. `technicians.proficiencies` is a Record<string, number>:
        // it supplies a category and a score and NOTHING else. It used to be
        // adapted with `confidence_score: 0.5` and `sample_size: 0`, which put
        // "Confidence: 50%" and "0 jobs" on the person's Passport about work
        // nobody had measured. CORE-C hosts this fact; it does not own the
        // metadata, and a missing confidence is not low confidence.
        // Core-100 R5 (CORE-C F/Prov): the live map carries no class; CORE-E's authority does, by the same rule the
        // portable writer applies. A failed read leaves the class unknown (nothing is said), never guessed.
        const classes = await fetchLiveProficiencyClasses(
          supabase, liveTech.id, liveTech.shop_id, Object.keys(liveProf),
        );
        return Object.entries(liveProf).map(([key, score]) => ({
          category_key: key,
          proficiency_score: Math.round(score),
          confidence_score: null,
          sample_size: null,
          provenance_class: classes ? classes[key] ?? null : null,
        }));
      }
      // Fall back to snapshot tables
      const { data, error } = await supabase
        .from('technician_proficiencies')
        .select('*')
        .eq('technician_profile_id', profileId!);
      if (error) throw error;
      return data?.map(p => ({
        category_key: p.category_key,
        proficiency_score: p.proficiency_score,
        confidence_score: p.confidence_score,
        // `|| 0` turned a genuinely NULL sample size into a factual "0 jobs".
        // The column is nullable and the sync trigger never writes it, so null
        // is "we don't know how much work", not "no work". A real 0 survives.
        sample_size: p.sample_size ?? null,
        // Core-100 R5 (CORE-C F/Prov): the class the single portable writer recorded at rest.
        provenance_class: portableProvenanceClassOf((p as { provenance_class?: string | null }).provenance_class),
      })) || [];
    },
    enabled: !!profileId && liveIdentitySettled,
  });

  const { data: metrics, isError: metricsFailed } = useDirectTechnicianMetrics(profileId || '');

  // Self-view completeness: the SAME hook and the SAME argument the
  // cross-person Passport uses, so the subject reads the identical fact
  // rather than a second approximation of it.
  const pensieveQuery = useTechnicianPensieveStats(profileId);

  // ── THE WORK LENS ────────────────────────────────────────────────────────
  //
  // Which capacities may this person's Passport be read through, and which one
  // is being read now. Both answers come from the canonical resolver; this page
  // decides nothing about eligibility itself.
  //
  // `requestedLens` is the viewer's CHOICE and is deliberately allowed to hold
  // a lens that is not (yet) available — `selectPassportWorkLens` refuses it
  // and falls back, so a lens that disappears because a role was revoked
  // mid-session cannot leave the page showing evidence under a heading that no
  // longer applies. It is component state. Nothing is persisted, and no server
  // call is made when it changes.
  const {
    resolution: lensResolution,
    detailWork,
    evidenceReadFailed: lensEvidenceFailed,
    isLoading: lensLoading,
  } = usePassportWorkLenses(profileId);
  const [requestedLens, setRequestedLens] = useState<PassportWorkLens | null>(null);
  const activeLens = selectPassportWorkLens(lensResolution, requestedLens);

  // Snapshot and Future Insights both COMPUTE claims about the person from
  // proficiencies and metrics together, so either read failing is enough to
  // make those claims unsafe to draw.
  const derivedInputsFailed = proficienciesFailed || metricsFailed;

  // Resolve title — hooks must be before conditional returns
  const resolvedTitle = resolveTechnicianTypeTitle(
    profile as any,
    passportProfile as any,
    proficiencies || [],
  );

  const queryClient = useQueryClient();

  // Guardrail #2: Persist inferred title if computed differs from stored
  useEffect(() => {
    if (!profile || !resolvedTitle.inferredTypeKey) return;
    if ((profile as any).technician_type_source === 'shop_verified') return;
    const stored = (profile as any).technician_type_inferred;
    if (stored === resolvedTitle.inferredTypeKey) return;

    (supabase.from('technician_profiles') as any)
      .update({
        technician_type_inferred: resolvedTitle.inferredTypeKey,
        technician_type_inferred_at: clock.now().toISOString(),
        technician_type_source: 'inferred',
      })
      .eq('id', profile.id)
      .then();
  }, [profile?.id, resolvedTitle.inferredTypeKey]);

  const handleSelfReportTypeChange = async (typeKey: TechnicianTypeKey) => {
    await (supabase.from('passport_profiles') as any)
      .update({
        technician_type_self_reported: typeKey,
        technician_type_self_reported_at: clock.now().toISOString(),
      })
      .eq('user_id', user!.id);
    queryClient.invalidateQueries({ queryKey: ['passport-profile'] });
  };

  if (profileLoading || passportLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading your passport...</p>
        </div>
      </div>
    );
  }

  // No technician profile — check for passport_profiles
  if (!profile) {
    // Has passport profile but no technician profile — simplified passport view
    if (passportProfile) {
      return (
        <div className="container mx-auto p-4 md:p-6 space-y-6 max-w-6xl pb-20 overflow-y-auto">
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg">Passport Mode</CardTitle>
                </div>
                <div className="flex gap-2">
                  {userShops.length > 0 && (
                    <Button variant="outline" size="sm" onClick={() => navigate('/')}>
                      <Home className="h-4 w-4 mr-1.5" />
                      Home
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => navigate('/account-settings')}>
                    Account Settings
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => signOut()}>
                    Sign Out
                  </Button>
                </div>
              </div>
              <CardDescription>
                {userShops.length > 0
                  ? 'Your professional passport. You are connected to a shop.'
                  : 'Your professional passport. You are not currently attached to a shop.'}
              </CardDescription>
            </CardHeader>
          </Card>

          <PassportConnectToggle />

          {/* Slice 4E.1: self-view Advisor Activity. Internally gated by */}
          {/* `advisorTimingAnalytics`; renders nothing when off. */}
          {/* Activity-based — counts/timing apply to any user (OWNER, */}
          {/* MANAGER, SERVICE_WRITER, ORG_ADMIN, TECHNICIAN) who performs */}
          {/* FOH work. Not title-based. */}
          <ErrorBoundary fallback={<PassportSectionFallback sectionName="Advisor Activity" />}>
            <AdvisorActivityCard advisorUserId={user!.id} />
          </ErrorBoundary>

          {/* Certifications + Application Sections (decoupled) */}
          <PassportCertificationsSection userId={user!.id} isOwner={true} />
          <PassportExperienceSection userId={user!.id} isOwner={true} />
          <PassportEducationSection userId={user!.id} isOwner={true} />
          <PassportReferencesSection userId={user!.id} isOwner={true} />
          <PassportEligibilitySection userId={user!.id} isOwner={true} />

          {/* Core-100 R2 (CORE-Q): an unattached person sees who can see their Passport too —
              found missing from this branch by the R2 real-stack browser proof (B5). */}
          <ErrorBoundary fallback={<PassportSectionFallback sectionName="Who can see your Passport" />}>
            <PassportAudienceCard />
          </ErrorBoundary>

          {/* School enrollment + verifications for student role */}
          {passportProfile.roles?.includes('student') && (
            <>
              <PassportSchoolSection />
              <StudentVerificationSummary studentUserId={user!.id} />
            </>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{passportProfile.display_name || user?.email}</CardTitle>
              <CardDescription>
                {passportProfile.roles?.length > 0
                  ? passportProfile.roles.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')
                  : 'No roles set'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3">
                <Button
                  variant="outline"
                  className="justify-start gap-3 h-auto py-4"
                  onClick={() => navigate('/connect')}
                >
                  <Rocket className="h-5 w-5 text-muted-foreground" />
                  <div className="text-left">
                    <div className="font-medium">OptiBay Connect</div>
                    <div className="text-sm text-muted-foreground">Manage visibility and find shops</div>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="justify-start gap-3 h-auto py-4"
                  onClick={() => navigate('/account-settings')}
                >
                  <User className="h-5 w-5 text-muted-foreground" />
                  <div className="text-left">
                    <div className="font-medium">Account Settings</div>
                    <div className="text-sm text-muted-foreground">Manage your profile and preferences</div>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <p className="text-sm font-medium text-foreground mb-2">How to join a shop:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Ask a shop manager to send you an invitation</li>
                <li>• Check your email for pending invitation links</li>
                <li>• Contact support if you need assistance</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      );
    }

    // No technician profile AND no passport profile — Start Your Passport.
    //
    // Lock the role picker when the user has a server-verified shop
    // membership (e.g. immediately after the beta invite flow created
    // their OWNER/MANAGER/SERVICE_WRITER membership in
    // provision-beta-org). `membershipRoleToPassportRole` returns null
    // for memberships that have no Passport-side identity (DISPATCHER,
    // PARTS, AUDITOR) — those fall through to free selection.
    //
    // Authoritative input is `usePermissions().role`, which reads from
    // the in-context membership. We deliberately do NOT consult
    // localStorage `beta_invite_data.role_requested` here: that field
    // is a display-only echo and has been cleared by the time
    // BetaComplete navigates onward. The membership row is the
    // canonical, server-verified source.
    return (
      <StartPassportFlow
        userEmail={user?.email ?? ''}
        onCreated={() => {}}
        createPassport={createPassport}
        signOut={signOut}
        navigate={navigate}
        invitedPassportRole={invitedPassportRole}
      />
    );
  }

  // Profile found — render full Technician Passport inline
  return (
    <div className="container mx-auto p-4 md:p-6 space-y-6 max-w-6xl pb-20 overflow-y-auto">
      {/* Passport Mode Banner */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Passport Mode</CardTitle>
            </div>
            <div className="flex gap-2 flex-wrap">
              <SchoolPortalButton navigate={navigate} />
              <AdminSchoolsButton navigate={navigate} />
              {userShops.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => navigate('/')}>
                  <Home className="h-4 w-4 mr-1.5" />
                  Home
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/account-settings')}
              >
                Account Settings
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => signOut()}
              >
                Sign Out
              </Button>
            </div>
          </div>
          <CardDescription>
            {userShops.length > 0
              ? 'Your full training passport is shown below.'
              : 'You are not currently attached to a shop. Your full training passport is shown below.'}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* OptiBay Connect Visibility Toggle */}
      <PassportConnectToggle />

      {/* WHAT YOU ARE LOOKING AT, not what you are.
          The control is presentation only: it selects which of this person's
          work capacities the evidence below is drawn for. It performs no
          mutation and makes no server call. Eligibility — and whether each
          lens is CURRENT or HISTORICAL — comes from the canonical resolver,
          never from this page. */}
      {!lensLoading && (
        <PassportWorkLensSwitcher
          resolution={lensResolution}
          selected={activeLens}
          onSelect={setRequestedLens}
          evidenceReadFailed={lensEvidenceFailed}
        />
      )}

      <PassportLensSection sectionId="snapshot-card" lens={activeLens}>
  <ErrorBoundary fallback={<PassportSectionFallback sectionName="Snapshot" />}>
          <PassportSnapshotCard
            profile={profile}
            proficiencies={proficiencies || []}
            metrics={metrics || null}
            derivedInputsFailed={derivedInputsFailed}
            technicianTypeTitle={resolvedTitle.displayTitle}
            technicianTypeSource={resolvedTitle.source}
            technicianTypeIsLocked={resolvedTitle.isLocked}
            onTechnicianTypeChange={!resolvedTitle.isLocked ? handleSelfReportTypeChange : undefined}
          />
        </ErrorBoundary>
      </PassportLensSection>

      {/* Self-view completeness, 2026-09-13. These three Pensieve sections
          rendered to a manager on /technicians/:id and were absent here, so a
          manager could see them and their subject could not. None carries any
          manager-only intent - no audience prop, no manager branch, no
          "technicians do not see" copy - so the omission was an omission. */}
      <PassportLensSection sectionId="pensieve-summary" lens={activeLens}>
  <PassportSelfViewFloor
          tierFeature="technicianPassports"
          fact="OptiTime learning coverage; shown to a manager on the cross-person Passport"
        >
          <ErrorBoundary fallback={<PassportSectionFallback sectionName="Pensieve Summary" />}>
            <PassportPensieveSummary stats={pensieveQuery.data} className="mt-1" />
          </ErrorBoundary>
        </PassportSelfViewFloor>
      </PassportLensSection>

      <PassportLensSection sectionId="employment-history" lens={activeLens}>
  <ErrorBoundary fallback={<PassportSectionFallback sectionName="Employment History" />}>
          <PassportEmploymentHistory
            employmentHistory={employmentHistory || []}
            isError={employmentHistoryFailed}
          />
        </ErrorBoundary>
      </PassportLensSection>

      <PassportLensSection sectionId="role-tracks" lens={activeLens}>
  <PassportSelfViewFloor
          tierFeature="passportAdvancedIntelligence"
          fact="the roles this person has held; shown to a manager on the cross-person Passport"
        >
          <ErrorBoundary fallback={<PassportSectionFallback sectionName="Role Tracks" />}>
            <PassportRoleTracks technicianProfileId={profileId!} optionalUserId={user!.id} />
          </ErrorBoundary>
        </PassportSelfViewFloor>
      </PassportLensSection>

      {/* The Detail Specialist lens. It reports what was done from the FROZEN
          attribution and scores nothing, because nothing in the product scores
          detail quality yet. See the component header. */}
      <PassportLensSection sectionId="detail-work-evidence" lens={activeLens}>
        <ErrorBoundary fallback={<PassportSectionFallback sectionName="Detail Work" />}>
          <PassportDetailWorkEvidence
            evidence={detailWork}
            isError={lensEvidenceFailed}
            isLoading={lensLoading}
          />
        </ErrorBoundary>
      </PassportLensSection>

      <PassportLensSection sectionId="proficiencies" lens={activeLens}>
  <ErrorBoundary fallback={<PassportSectionFallback sectionName="Proficiencies" />}>
          {/* Roadmap #9: the person's own background, editable by them
              (self-reported), beside what OptiBay has observed. */}
          <div className="mb-4">
            <TechnicianBackgroundCard
              shopId={(liveTech as { shop_id?: string } | null)?.shop_id ?? null}
              shopTechnicianId={liveTech?.id ?? null}
              optibaySince={(employmentHistory || []).reduce<string | null>(
              (min, e: any) => (e?.start_date && (!min || e.start_date < min) ? e.start_date : min), null)}
              completedJobs={metrics?.jobs_completed ?? null}
              canEdit
              perspective="self"
            />
          </div>
          <PassportProficiencySection
            proficiencies={proficiencies || []}
            technicianProfileId={profileId!}
            isError={proficienciesFailed}
          />
        </ErrorBoundary>
      </PassportLensSection>

      {/* CORE-C-GAP-001, the surface half. This section rendered on the
          manager view of a person and not on the person’s own Passport,
          which is what the constitution calls a file rather than a record.
          `isSelfView` is unconditionally true here because this branch IS
          the subject reading themselves. The database half landed first
          (20261114130000); before it this card would have been empty. */}
      <PassportLensSection sectionId="diagnostic-competency" lens={activeLens}>
  <PassportSelfViewFloor
          tierFeature="pensieveLearning"
          fact="the person's diagnostic competency score; shown to a manager on the cross-person Passport"
        >
          <ErrorBoundary fallback={<PassportSectionFallback sectionName="Diagnostic Competency" />}>
            <PassportDiagnosticCompetency
              technicianProfileId={profileId!}
              isManagerView={false}
              isSelfView
            />
          </ErrorBoundary>
        </PassportSelfViewFloor>
      </PassportLensSection>

      <PassportLensSection sectionId="skill-heatmap" lens={activeLens}>
  <ErrorBoundary fallback={<PassportSectionFallback sectionName="Skill Heatmap" />}>
          <PassportSkillHeatmap
            technicianProfileId={profileId || undefined}
            liveShopTechnicianId={liveTech?.id}
            liveProficiencies={
              (liveTech?.proficiencies as Record<string, number> | null) ?? null
            }
          />
        </ErrorBoundary>
      </PassportLensSection>

      <PassportLensSection sectionId="performance-dna" lens={activeLens}>
  <PassportSelfViewFloor
          tierFeature="passportAdvancedIntelligence"
          fact="the person's own efficiency and comeback figures; shown to a manager on the cross-person Passport"
        >
          <ErrorBoundary fallback={<PassportSectionFallback sectionName="Performance DNA" />}>
            <PassportPerformanceDNA
              technicianProfileId={profileId!}
              liveShopTechnicianId={liveTech?.id}
              liveIdentityReady={liveIdentitySettled}
            />
          </ErrorBoundary>
        </PassportSelfViewFloor>
      </PassportLensSection>

      <PassportLensSection sectionId="time-signature" lens={activeLens}>
  <PassportSelfViewFloor
          tierFeature="passportAdvancedIntelligence"
          fact="how the person's own recorded minutes were spent; shown to a manager on the cross-person Passport"
        >
          <ErrorBoundary fallback={<PassportSectionFallback sectionName="Time Signature" />}>
            <PassportTimeSignature technicianProfileId={profileId!} />
          </ErrorBoundary>
        </PassportSelfViewFloor>
      </PassportLensSection>

      <PassportLensSection sectionId="inspection-metrics" lens={activeLens}>
  <ErrorBoundary fallback={<PassportSectionFallback sectionName="Inspection Metrics" />}>
          <PassportInspectionMetrics technicianProfileId={profileId!} />
        </ErrorBoundary>
      </PassportLensSection>

      <PassportLensSection sectionId="pensieve-insights" lens={activeLens}>
  <PassportSelfViewFloor
          tierFeature="technicianPassports"
          fact="OptiTime learning detail; shown to a manager on the cross-person Passport"
        >
          <ErrorBoundary fallback={<PassportSectionFallback sectionName="Pensieve Insights" />}>
            <PassportPensieveInsights stats={pensieveQuery.data} isLoading={pensieveQuery.isLoading} />
          </ErrorBoundary>
        </PassportSelfViewFloor>
      </PassportLensSection>

      <PassportLensSection sectionId="pensieve-coaching" lens={activeLens}>
  <PassportSelfViewFloor
          tierFeature="technicianPassports"
          fact="OptiTime coaching detail; shown to a manager on the cross-person Passport"
        >
          <ErrorBoundary fallback={<PassportSectionFallback sectionName="Pensieve Coaching" />}>
            <PassportPensieveCoaching stats={pensieveQuery.data} isLoading={pensieveQuery.isLoading} />
          </ErrorBoundary>
        </PassportSelfViewFloor>
      </PassportLensSection>

      <PassportLensSection sectionId="ticket-ledger" lens={activeLens}>
  <ErrorBoundary fallback={<PassportSectionFallback sectionName="Ticket Ledger" />}>
          <PassportTicketLedger technicianProfileId={profileId!} />
        </ErrorBoundary>
      </PassportLensSection>

      <ErrorBoundary fallback={<PassportSectionFallback sectionName="Toolbox" />}>
        <ToolboxSection technicianProfileId={profileId!} />
      </ErrorBoundary>

      {/* Slice 4E.1: self-view Advisor Activity. Internally gated by */}
      {/* `advisorTimingAnalytics`; renders nothing when off. */}
      {/* Activity-based — counts/timing apply to any user (OWNER, */}
      {/* MANAGER, SERVICE_WRITER, ORG_ADMIN, TECHNICIAN) who performs */}
      {/* FOH work. Not title-based. */}
      <PassportLensSection sectionId="advisor-activity" lens={activeLens}>
  <ErrorBoundary fallback={<PassportSectionFallback sectionName="Advisor Activity" />}>
          <AdvisorActivityCard advisorUserId={user!.id} />
        </ErrorBoundary>
      </PassportLensSection>

      <ErrorBoundary fallback={<PassportSectionFallback sectionName="Certifications" />}>
        <PassportCertificationsSection userId={user!.id} isOwner={true} />
      </ErrorBoundary>

      <ErrorBoundary fallback={<PassportSectionFallback sectionName="Experience" />}>
        <PassportExperienceSection userId={user!.id} isOwner={true} />
      </ErrorBoundary>

      <ErrorBoundary fallback={<PassportSectionFallback sectionName="Education" />}>
        <PassportEducationSection userId={user!.id} isOwner={true} />
      </ErrorBoundary>

      <ErrorBoundary fallback={<PassportSectionFallback sectionName="References" />}>
        <PassportReferencesSection userId={user!.id} isOwner={true} />
      </ErrorBoundary>

      <ErrorBoundary fallback={<PassportSectionFallback sectionName="Eligibility" />}>
        <PassportEligibilitySection userId={user!.id} isOwner={true} />
      </ErrorBoundary>

      {passportProfile?.roles?.includes('student') && (
        <ErrorBoundary fallback={<PassportSectionFallback sectionName="School" />}>
          <PassportSchoolSection />
          <StudentVerificationSummary studentUserId={user!.id} />
        </ErrorBoundary>
      )}

      <PassportLensSection sectionId="future-insights" lens={activeLens}>
  <PassportSelfViewFloor
          tierFeature="passportAdvancedIntelligence"
          fact="insights derived from the person's own proficiencies and metrics; shown to a manager on the cross-person Passport"
        >
          <ErrorBoundary fallback={<PassportSectionFallback sectionName="Future Insights" />}>
            <PassportFutureInsights
              proficiencies={proficiencies || []}
              metrics={metrics || null}
              isError={derivedInputsFailed}
            />
          </ErrorBoundary>
        </PassportSelfViewFloor>
      </PassportLensSection>

      <PassportLensSection sectionId="learning-center" lens={activeLens}>
  <ErrorBoundary fallback={<PassportSectionFallback sectionName="Learning Center" />}>
          <PassportLearningCenter technicianProfileId={profileId!} proficiencies={proficiencies || []} isOwnProfile={true} />
        </ErrorBoundary>
      </PassportLensSection>

      {/* Mixed content, and the component already separates it: with
          `isManagerView={false}` the note text is read-only and the manager's
          editor is withheld. The subject sees the fact recorded about them,
          not the control for writing it. */}
      <PassportSelfViewFloor
        tierFeature="passportAdvancedIntelligence"
        fact="manager-authored coaching notes ABOUT this person; shown to a manager on the cross-person Passport"
      >
        <ErrorBoundary fallback={<PassportSectionFallback sectionName="Coaching Notes" />}>
          <PassportCoachingNotes technicianProfileId={profileId!} initialNotes={profile.notes} isManagerView={false} />
        </ErrorBoundary>
      </PassportSelfViewFloor>

      {/* Core-100 R2 (CORE-Q): who can see this Passport, on what basis, for how long — and who looked. */}
      <ErrorBoundary fallback={<PassportSectionFallback sectionName="Who can see your Passport" />}>
        <PassportAudienceCard />
      </ErrorBoundary>
    </div>
  );
};

export default PassportHome;
